import { Injectable, computed, effect, inject, signal } from '@angular/core';
import { Preferences } from '@capacitor/preferences';
import { Callable, FnRegion } from './callable';
import { NetworkService } from './network';
import { ToastService } from './toast';
import { Auth } from './auth';
import { OutboxBlobs } from './outbox-blobs';
import { Storage } from './storage';

/**
 * File d'attente durable des mutations qui passent par un Cloud Function.
 *
 * Pourquoi elle existe — et pourquoi elle ne double PAS `OfflineWriteService` :
 * le cache persistant de Firestore met déjà en file les écritures *directes*
 * (`setDoc`, `updateDoc`) et les rejoue tout seul. Il ne fait rien pour un
 * `httpsCallable`, qui échoue sèchement hors ligne. Or les deux écritures du
 * cœur métier — enregistrer une **Cotisation** et une **Dépense** — sont
 * précisément des callables, parce que le serveur seul fixe le taux de change et
 * contrôle les rôles. Un collecteur sans réseau ne pouvait donc rien saisir.
 *
 * Ce que la file garantit :
 *  • la saisie survit à la fermeture de l'application (persistée en Preferences) ;
 *  • elle part **exactement une fois** — chaque entrée porte un UUID v4 utilisé
 *    comme `clientMutationId`, que le serveur transforme en identifiant du
 *    document écrit ; rejouer ne peut donc ni dupliquer l'écriture, ni recompter
 *    un total, ni débiter la caisse deux fois ;
 *  • un refus définitif du serveur n'est jamais avalé en silence : l'entrée est
 *    retirée ET l'utilisateur est prévenu.
 *
 * Elle sert aussi le réseau *dégradé*, pas seulement le réseau absent : une
 * tentative en ligne qui échoue sur un timeout bascule en file plutôt que de
 * perdre la saisie. C'est sûr parce que la clé d'idempotence est décidée AVANT
 * l'appel — si la première requête avait en fait abouti côté serveur, le rejeu
 * est un no-op.
 */

const STORAGE_KEY = 'te.outbox';

/** Au-delà, on cesse de réessayer tout seul et on le dit à l'utilisateur. */
const MAX_ATTEMPTS = 8;

/** Plafond du délai entre deux tentatives automatiques. */
const MAX_RETRY_DELAY_MS = 5 * 60_000;

/**
 * Codes qu'un réessai ne réparera pas : la requête est refusée sur le fond
 * (montant invalide, rôle retiré, événement clôturé, plancher du **Montant
 * attendu** non atteint…). Tout le reste — `unavailable`, `deadline-exceeded`,
 * `internal`, `unauthenticated` le temps que la session revienne, ou une erreur
 * réseau nue — est transitoire et mérite d'être rejoué.
 */
const PERMANENT_CODES = new Set([
  'invalid-argument',
  'permission-denied',
  'failed-precondition',
  'not-found',
  'already-exists',
  'out-of-range',
]);

export type OutboxKind = 'contribution' | 'expense';

/** Ce qu'un service métier demande à envoyer, sans se soucier du réseau. */
export interface OutboxSpec {
  readonly kind: OutboxKind;
  readonly eventId: string;
  readonly region: FnRegion;
  readonly fn: string;
  readonly payload: Record<string, unknown>;
  /** Libellé court montré dans la liste « en attente » (« Marie Dupont · 50 $ »). */
  readonly label: string;
  /**
   * Photo prise hors ligne, à téléverser juste avant l'appel. Firebase Storage
   * n'a pas de file d'attente : l'image attend donc ici, et son URL n'existera
   * qu'au moment de la synchronisation.
   */
  readonly photoDataUrl?: string;
}

export interface OutboxEntry extends OutboxSpec {
  /** UUID v4 : clé d'idempotence ET futur identifiant du document créé. */
  readonly id: string;
  /** Heure de la saisie, transmise au serveur comme date de survenue réelle. */
  readonly createdAt: number;
  attempts: number;
  lastError?: string;
  /** Plus aucune tentative automatique : il faut une action de l'utilisateur. */
  stalled?: boolean;
  /** Clé de la photo conservée dans {@link OutboxBlobs}, le cas échéant. */
  blobKey?: string;
}

export interface OutboxSendResult {
  /** `true` quand la mutation attend le réseau, `false` quand le serveur l'a acceptée. */
  readonly queued: boolean;
  /** Identifiant de l'écriture — le même en file et une fois synchronisée. */
  readonly id: string;
  readonly result?: unknown;
}

/** Un échec de callable qui ne s'arrangera pas en réessayant. */
export function isPermanentFailure(error: unknown): boolean {
  const raw = (error as { code?: unknown } | null)?.code;
  // Erreur réseau nue (pas de code) : par définition transitoire.
  if (typeof raw !== 'string') return false;
  const code = raw.startsWith('functions/') ? raw.slice('functions/'.length) : raw;
  return PERMANENT_CODES.has(code);
}

function messageOf(error: unknown): string {
  const msg = (error as { message?: unknown } | null)?.message;
  return typeof msg === 'string' && msg.trim() ? msg : 'Erreur inconnue';
}

const LABELS: Record<OutboxKind, string> = {
  contribution: 'Cotisation',
  expense: 'Dépense',
};

@Injectable({ providedIn: 'root' })
export class OutboxService {
  private readonly callable = inject(Callable);
  private readonly network = inject(NetworkService);
  private readonly toast = inject(ToastService);
  private readonly auth = inject(Auth);
  private readonly blobs = inject(OutboxBlobs);
  private readonly storage = inject(Storage);

  private readonly _entries = signal<readonly OutboxEntry[]>([]);
  readonly entries = this._entries.asReadonly();
  readonly pendingCount = computed(() => this._entries().length);

  private flushing = false;
  private retryTimer: ReturnType<typeof setTimeout> | null = null;

  constructor() {
    // Le retour du réseau est le déclencheur principal : rien à orchestrer
    // ailleurs, le signal suffit.
    effect(() => {
      if (this.network.isOnline()) void this.flush();
    });
  }

  /** Entrées en attente pour un événement, dans l'ordre de saisie. */
  entriesFor(eventId: string): readonly OutboxEntry[] {
    return this._entries().filter((e) => e.eventId === eventId);
  }

  /** Recharge la file persistée et tente un premier envoi. Appelé au démarrage. */
  async init(): Promise<void> {
    this._entries.set(await this.load());
    void this.flush();
  }

  /**
   * Envoie une mutation, ou la met en file si le réseau ne permet pas de la
   * conclure maintenant. C'est le seul point d'entrée des services métier :
   * eux n'ont pas à savoir s'il y a du réseau.
   */
  async send(spec: OutboxSpec): Promise<OutboxSendResult> {
    const { photoDataUrl, ...rest } = spec;
    const entry: OutboxEntry = {
      ...rest,
      id: crypto.randomUUID(),
      createdAt: Date.now(),
      attempts: 0,
    };

    if (!this.network.isOnline()) {
      await this.enqueue(await this.withPhoto(entry, photoDataUrl));
      return { queued: true, id: entry.id };
    }

    try {
      const payload = photoDataUrl
        ? { ...entry, payload: await this.attachPhoto(entry, photoDataUrl) }
        : entry;
      return { queued: false, id: entry.id, result: await this.dispatch(payload) };
    } catch (error) {
      // Refus sur le fond : c'est au formulaire de le montrer, pas à la file.
      if (isPermanentFailure(error)) throw error;
      // Réseau annoncé disponible mais inexploitable (2G saturée, portail
      // captif, coupure en plein vol) : la saisie part en file plutôt qu'à la
      // poubelle. Le rejeu est sûr, la clé d'idempotence existe déjà.
      const queued = await this.withPhoto(
        { ...entry, attempts: 1, lastError: messageOf(error) },
        photoDataUrl
      );
      await this.enqueue(queued);
      return { queued: true, id: entry.id };
    }
  }

  /**
   * Rejoue la file, une entrée à la fois et dans l'ordre de saisie.
   *
   * Le verrou est indispensable : les déclencheurs (retour réseau, démarrage,
   * nouvelle saisie, minuterie de réessai) peuvent se chevaucher, et deux flushs
   * concurrents enverraient deux fois la même entrée.
   */
  async flush(): Promise<void> {
    if (this.flushing || !this._entries().length) return;
    if (!this.network.isOnline()) return;

    // Le verrou est posé AVANT le premier `await` : le poser après laisserait
    // deux appels concurrents franchir la garde ci-dessus et envoyer la même
    // entrée deux fois.
    this.flushing = true;
    try {
      // Un callable sans session part en `unauthenticated` : mieux vaut attendre.
      await this.auth.authReady;
      if (!this.auth.currentUser()) return;

      for (const entry of [...this._entries()]) {
        if (entry.stalled || !this.network.isOnline()) continue;
        const outcome = await this.attempt(entry);
        // Panne serveur ou réseau reperdu : inutile d'insister sur les
        // suivantes, elles échoueraient toutes de la même façon.
        if (outcome === 'retry') break;
      }
    } finally {
      this.flushing = false;
    }
  }

  /** Retire une entrée définitivement bloquée, à la demande de l'utilisateur. */
  async discard(id: string): Promise<void> {
    const entry = this._entries().find((e) => e.id === id);
    if (entry?.blobKey) await this.blobs.remove(entry.blobKey);
    await this.persist(this._entries().filter((e) => e.id !== id));
  }

  /** Relance une entrée bloquée après épuisement des tentatives. */
  async retry(id: string): Promise<void> {
    await this.persist(
      this._entries().map((e) => (e.id === id ? { ...e, attempts: 0, stalled: false } : e))
    );
    void this.flush();
  }

  // ─── Interne ──────────────────────────────────────────────────────────────

  private async attempt(entry: OutboxEntry): Promise<'done' | 'retry' | 'dropped'> {
    try {
      await this.dispatch(entry);
      await this.persist(this._entries().filter((e) => e.id !== entry.id));
      return 'done';
    } catch (error) {
      if (isPermanentFailure(error)) {
        // Jamais de suppression muette : la saisie disparaît de la file, donc
        // l'utilisateur doit savoir qu'elle n'a pas été enregistrée.
        if (entry.blobKey) await this.blobs.remove(entry.blobKey);
        await this.persist(this._entries().filter((e) => e.id !== entry.id));
        this.toast.error(`${LABELS[entry.kind]} refusée : ${messageOf(error)}`);
        return 'dropped';
      }

      const attempts = entry.attempts + 1;
      const stalled = attempts >= MAX_ATTEMPTS;
      await this.persist(
        this._entries().map((e) =>
          e.id === entry.id ? { ...e, attempts, stalled, lastError: messageOf(error) } : e
        )
      );
      if (stalled) {
        this.toast.warning(
          `${
            LABELS[entry.kind]
          } toujours pas synchronisée. Vérifiez la liste des actions en attente.`
        );
      } else {
        this.scheduleRetry(attempts);
      }
      return 'retry';
    }
  }

  private async dispatch(entry: OutboxEntry): Promise<unknown> {
    // La photo part d'abord : le serveur refuse une dépense réglée au-dessus du
    // seuil sans justificatif, donc l'ordre n'est pas négociable.
    const payload = entry.blobKey
      ? await this.attachStoredPhoto(entry)
      : { ...entry.payload, clientMutationId: entry.id, occurredAt: entry.createdAt };

    const result = await this.callable.call(entry.region, entry.fn, payload);
    if (entry.blobKey) await this.blobs.remove(entry.blobKey);
    return result;
  }

  /** Range la photo à côté de l'entrée mise en file. */
  private async withPhoto(entry: OutboxEntry, dataUrl?: string): Promise<OutboxEntry> {
    if (!dataUrl) return entry;
    await this.blobs.put(entry.id, dataUrl);
    return { ...entry, blobKey: entry.id };
  }

  private async attachPhoto(entry: OutboxEntry, dataUrl: string): Promise<Record<string, unknown>> {
    const { url, path } = await this.storage.uploadReceiptPhoto(entry.eventId, dataUrl);
    return { ...entry.payload, receiptPhotoUrl: url, receiptPhotoPath: path };
  }

  private async attachStoredPhoto(entry: OutboxEntry): Promise<Record<string, unknown>> {
    const base = { ...entry.payload, clientMutationId: entry.id, occurredAt: entry.createdAt };
    const dataUrl = await this.blobs.get(entry.blobKey as string);
    // Photo introuvable (stockage purgé par le système) : on envoie quand même
    // l'écriture. Perdre le justificatif est regrettable ; perdre la dépense le
    // serait bien davantage, et elle reste rattrapable par « Déposer la facture ».
    if (!dataUrl) return base;
    const { url, path } = await this.storage.uploadReceiptPhoto(entry.eventId, dataUrl);
    return { ...base, receiptPhotoUrl: url, receiptPhotoPath: path };
  }

  private async enqueue(entry: OutboxEntry): Promise<void> {
    await this.persist([...this._entries(), entry]);
  }

  private scheduleRetry(attempts: number): void {
    if (this.retryTimer) return;
    const delay = Math.min(MAX_RETRY_DELAY_MS, 2 ** attempts * 1000);
    this.retryTimer = setTimeout(() => {
      this.retryTimer = null;
      void this.flush();
    }, delay);
  }

  private async persist(entries: readonly OutboxEntry[]): Promise<void> {
    this._entries.set(entries);
    try {
      await Preferences.set({ key: STORAGE_KEY, value: JSON.stringify(entries) });
    } catch {
      // Stockage indisponible : la file vit au moins le temps de la session.
      // Perdre la persistance ne doit pas faire perdre la saisie en cours.
    }
  }

  private async load(): Promise<readonly OutboxEntry[]> {
    try {
      const { value } = await Preferences.get({ key: STORAGE_KEY });
      if (!value) return [];
      const parsed: unknown = JSON.parse(value);
      if (!Array.isArray(parsed)) return [];
      return parsed.filter(
        (e: unknown): e is OutboxEntry =>
          typeof (e as OutboxEntry)?.id === 'string' && typeof (e as OutboxEntry)?.fn === 'string'
      );
    } catch {
      return [];
    }
  }
}
